import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import {
  Package,
  RefreshCw,
  Search,
  ExternalLink,
  CheckCircle,
  XCircle,
  Copy,
  Upload,
  Play,
  Eye,
  Trash2,
  AlertCircle,
  Plus,
  TrendingUp,
  DollarSign,
} from 'lucide-react';

interface Shop {
  id: string;
  shopName: string;
  platformType: string;
}

interface AmazonProduct {
  asin: string;
  title: string;
  price: number;
  images: string[];
  description: string;
  brand: string;
  category: string;
  features: string[];
  stock: number;
  availability: string;
  rating: string;
  reviewCount: number;
}

interface Listing {
  id: string;
  asin: string;
  rakutenItemCode: string;
  rakutenItemName: string;
  amazonPrice: string;
  rakutenPrice: string;
  amazonStock: number;
  rakutenStock: number;
  priceMarkup: string;
  autoSync: boolean;
  status: string;
  lastSyncAt: string;
  createdAt: string;
}

interface ListingLog {
  id: string;
  action: string;
  status: string;
  message: string;
  createdAt: string;
}

type TabType = 'search' | 'listings' | 'bulk';

const STATUS_LABELS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  pending: { label: '準備中', color: 'bg-yellow-100 text-yellow-700', icon: <Clock size={14} /> },
  active: { label: '出品中', color: 'bg-green-100 text-green-700', icon: <CheckCircle size={14} /> },
  delisted: { label: '停止', color: 'bg-gray-100 text-gray-600', icon: <XCircle size={14} /> },
  error: { label: 'エラー', color: 'bg-red-100 text-red-700', icon: <AlertCircle size={14} /> },
};

function Clock({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

export default function AmazonToRakutenPage() {
  const [activeTab, setActiveTab] = useState<TabType>('search');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ショップ
  const [amazonShops, setAmazonShops] = useState<Shop[]>([]);
  const [rakutenShops, setRakutenShops] = useState<Shop[]>([]);
  const [selectedAmazonShop, setSelectedAmazonShop] = useState('');
  const [selectedRakutenShop, setSelectedRakutenShop] = useState('');

  // ASIN検索
  const [asinInput, setAsinInput] = useState('');
  const [searchedProducts, setSearchedProducts] = useState<AmazonProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<AmazonProduct | null>(null);
  const [applyFilters, setApplyFilters] = useState(true); // フィルタ自動適用

  // 出品設定
  const [rakutenItemCode, setRakutenItemCode] = useState('');
  const [rakutenPrice, setRakutenPrice] = useState('');
  const [rakutenStock, setRakutenStock] = useState('');
  const [priceMarkup, setPriceMarkup] = useState('1.2');
  const [usePricingRules, setUsePricingRules] = useState(true); // 価格設定を使用
  const [calculatedPrice, setCalculatedPrice] = useState<any>(null);

  // 出品一覧
  const [listings, setListings] = useState<Listing[]>([]);
  const [stats, setStats] = useState<Record<string, number>>({});

  // 詳細モーダル
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [listingLogs, setListingLogs] = useState<ListingLog[]>([]);

  // 一括出品
  const [bulkAsins, setBulkAsins] = useState('');
  const [bulkPriceMarkup, setBulkPriceMarkup] = useState('1.2');

  // 同意確認文
  const [consentTexts, setConsentTexts] = useState<any[]>([]);
  const [applicableConsents, setApplicableConsents] = useState<any[]>([]);

  // 価格設定からの自動計算
  const calculateWithRules = useCallback(async (product: AmazonProduct) => {
    if (!usePricingRules) return;
    try {
      const res = await api.post('/api/pricing-rules/calculate', {
        amazonPrice: product.price,
        category: product.category,
        brand: product.brand,
        title: product.title,
        deliveryType: 'standard',
      });
      const data = await res.json();
      if (data.success) {
        setCalculatedPrice(data);
        setRakutenPrice(data.rakutenPrice.toString());
      }
    } catch (err) {
      console.error('Calculate price error:', err);
    }
  }, [usePricingRules]);

  // 同意確認文取得
  const fetchApplicableConsents = useCallback(async (title: string) => {
    try {
      const res = await api.post('/api/pricing-rules/get-consent-texts', { title });
      const data = await res.json();
      setApplicableConsents(data.texts || []);
    } catch (err) {
      console.error('Fetch consent texts error:', err);
    }
  }, []);

  // ショップ一覧取得
  const fetchShops = useCallback(async () => {
    try {
      const res = await api.get('/api/dropship/shops');
      const data = await res.json();
      setAmazonShops(data.amazonShops || []);
      setRakutenShops(data.rakutenShops || []);
      if (data.amazonShops?.length > 0) setSelectedAmazonShop(data.amazonShops[0].id);
      if (data.rakutenShops?.length > 0) setSelectedRakutenShop(data.rakutenShops[0].id);
    } catch (err) {
      console.error('Fetch shops error:', err);
    }
  }, []);

  // Amazon商品検索
  const searchProducts = async () => {
    const asins = asinInput
      .split(/[\s,\n]+/)
      .map(a => a.trim())
      .filter(a => a.length === 10);

    if (asins.length === 0) {
      setError('有効なASINを入力してください（10文字、例: B0795D2LXS）');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/api/amazon-to-rakuten/fetch-products', { asins });
      const data = await res.json();
      if (data.success) {
        let products = data.products;

        // フィルタ自動適用
        if (applyFilters) {
          try {
            const filterRes = await api.post('/api/product-filters/apply', {
              products: products.map((p: AmazonProduct) => ({
                asin: p.asin,
                title: p.title,
                description: p.description,
              })),
            });
            const filterData = await filterRes.json();
            
            // フィルタ結果を反映
            products = products.map((p: AmazonProduct, i: number) => {
              const filtered = filterData.products[i];
              return {
                ...p,
                title: filtered.title,
                description: filtered.description,
                isFiltered: filtered.isFiltered,
                filterReason: filtered.filterReason,
              };
            });

            // NGフィルタに引っかかった商品を除外
            products = products.filter((p: any) => !p.isFiltered);

            if (filterData.stats.filtered > 0) {
              alert(`${filterData.stats.filtered}件の商品がNGフィルタにより除外されました`);
            }
          } catch (err) {
            console.error('Filter error:', err);
          }
        }

        setSearchedProducts(products);
        if (products.length > 0) {
          setSelectedProduct(products[0]);
          setPriceMarkup('1.2');
          // 価格設定で計算
          if (usePricingRules) {
            await calculateWithRules(products[0]);
          }
          // 同意確認文を取得
          await fetchApplicableConsents(products[0].title);
        }
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Amazon商品の検索に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 楽天に出品
  const listToRakuten = async () => {
    if (!selectedProduct || !rakutenItemCode) {
      setError('必須項目を入力してください');
      return;
    }

    const finalPrice = rakutenPrice || Math.floor(selectedProduct.price * parseFloat(priceMarkup));
    const finalStock = rakutenStock || selectedProduct.stock;

    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/api/amazon-to-rakuten/list-to-rakuten', {
        amazonShopId: selectedAmazonShop,
        rakutenShopId: selectedRakutenShop,
        asin: selectedProduct.asin,
        rakutenItemCode,
        rakutenItemName: selectedProduct.title,
        rakutenPrice: finalPrice,
        rakutenStock: finalStock,
        rakutenDescription: selectedProduct.description,
        rakutenImages: selectedProduct.images,
        priceMarkup: parseFloat(priceMarkup),
        autoSync: true,
      });

      const data = await res.json();
      if (data.success) {
        alert('楽天に出品しました！');
        setRakutenItemCode('');
        setRakutenPrice('');
        setRakutenStock('');
        await fetchListings();
        await fetchStats();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('楽天出品中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  // 出品一覧取得
  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/amazon-to-rakuten/listings');
      const data = await res.json();
      setListings(data.listings || []);
    } catch (err) {
      setError('出品一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  // 統計取得
  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/api/amazon-to-rakuten/stats');
      const data = await res.json();
      setStats(data.stats || {});
    } catch (err) {
      console.error('Fetch stats error:', err);
    }
  }, []);

  // 在庫・価格同期
  const syncListing = async (id: string) => {
    try {
      const res = await api.post(`/api/amazon-to-rakuten/sync/${id}`, {});
      const data = await res.json();
      if (data.success) {
        alert(`同期完了: 価格¥${data.newPrice.toLocaleString()}, 在庫${data.newStock}`);
        await fetchListings();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('同期中にエラーが発生しました');
    }
  };

  // 出品停止
  const delistItem = async (id: string) => {
    if (!confirm('この商品の出品を停止しますか？')) return;
    try {
      const res = await api.put(`/api/amazon-to-rakuten/delist/${id}`, {});
      const data = await res.json();
      if (data.success) {
        await fetchListings();
        await fetchStats();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('出品停止中にエラーが発生しました');
    }
  };

  // ログ取得
  const fetchLogs = async (listingId: string) => {
    try {
      const res = await api.get(`/api/amazon-to-rakuten/logs/${listingId}`);
      const data = await res.json();
      setListingLogs(data.logs || []);
    } catch (err) {
      console.error('Fetch logs error:', err);
    }
  };

  // 一括出品
  const bulkList = async () => {
    const lines = bulkAsins.split('\n').filter(l => l.trim());
    if (lines.length === 0) {
      setError('ASINとコードを入力してください');
      return;
    }

    const items = lines.map(line => {
      const [asin, rakutenItemCode] = line.split(/[\t,]/).map(s => s.trim());
      return { asin, rakutenItemCode, priceMarkup: parseFloat(bulkPriceMarkup), stockBuffer: 5 };
    });

    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/api/amazon-to-rakuten/bulk-list', {
        amazonShopId: selectedAmazonShop,
        rakutenShopId: selectedRakutenShop,
        items,
      });

      const data = await res.json();
      if (data.success) {
        alert(data.message);
        setBulkAsins('');
        await fetchListings();
        await fetchStats();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('一括出品中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShops();
  }, [fetchShops]);

  useEffect(() => {
    if (activeTab === 'listings') {
      fetchListings();
      fetchStats();
    }
  }, [activeTab, fetchListings, fetchStats]);

  const renderStatusBadge = (status: string) => {
    const statusInfo = STATUS_LABELS[status] || { label: status, color: 'bg-gray-100', icon: null };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.icon}
        {statusInfo.label}
      </span>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Amazon→楽天コピー出品</h1>
          <p className="text-sm text-gray-500 mt-1">
            AmazonのASINを貼り付けるだけで、楽天に簡単出品
          </p>
        </div>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="text-red-500" size={20} />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <XCircle size={20} />
          </button>
        </div>
      )}

      {/* タブ */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('search')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'search'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <Search size={18} />
              ASIN検索・出品
            </span>
          </button>
          <button
            onClick={() => setActiveTab('listings')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'listings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <Package size={18} />
              出品一覧
              {stats.active > 0 && (
                <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {stats.active}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('bulk')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'bulk'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <Upload size={18} />
              一括出品
            </span>
          </button>
        </nav>
      </div>

      {/* ASIN検索・出品タブ */}
      {activeTab === 'search' && (
        <div className="space-y-6">
          {/* ショップ選択 */}
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h3 className="font-medium text-lg">ショップ設定</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amazonショップ</label>
                <select
                  value={selectedAmazonShop}
                  onChange={(e) => setSelectedAmazonShop(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {amazonShops.map(shop => (
                    <option key={shop.id} value={shop.id}>{shop.shopName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">楽天ショップ</label>
                <select
                  value={selectedRakutenShop}
                  onChange={(e) => setSelectedRakutenShop(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {rakutenShops.map(shop => (
                    <option key={shop.id} value={shop.id}>{shop.shopName}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* ASIN検索 */}
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h3 className="font-medium text-lg">Amazon商品検索</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ASIN（複数の場合は改行またはスペース区切り）
              </label>
              <textarea
                value={asinInput}
                onChange={(e) => setAsinInput(e.target.value)}
                placeholder="B0795D2LXS&#10;B0795D2KZR&#10;B0795D299L"
                className="w-full border rounded-lg px-3 py-2 h-24"
              />
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  checked={applyFilters}
                  onChange={(e) => setApplyFilters(e.target.checked)}
                  className="rounded"
                />
                <label className="text-sm text-gray-700">
                  NGフィルタと文言置換を自動適用（推奨）
                </label>
              </div>
            </div>
            <button
              onClick={searchProducts}
              disabled={loading || !asinInput}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Search size={16} />
              商品情報を取得
            </button>
          </div>

          {/* 検索結果 */}
          {searchedProducts.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <h3 className="font-medium text-lg">検索結果</h3>
              <div className="grid grid-cols-3 gap-4">
                {searchedProducts.map(product => (
                  <div
                    key={product.asin}
                    onClick={() => setSelectedProduct(product)}
                    className={`border rounded-lg p-4 cursor-pointer transition ${
                      selectedProduct?.asin === product.asin
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <img src={product.images[0]} alt={product.title} className="w-full h-32 object-contain mb-2" />
                    <div className="text-sm font-medium truncate">{product.title}</div>
                    <div className="text-xs text-gray-500 mt-1">ASIN: {product.asin}</div>
                    <div className="text-lg font-bold text-blue-600 mt-2">¥{product.price.toLocaleString()}</div>
                    <div className="text-xs text-gray-600">在庫: {product.stock}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 出品設定 */}
          {selectedProduct && (
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-lg">楽天出品設定</h3>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={usePricingRules}
                    onChange={(e) => {
                      setUsePricingRules(e.target.checked);
                      if (e.target.checked && selectedProduct) {
                        calculateWithRules(selectedProduct);
                      } else {
                        setCalculatedPrice(null);
                        setRakutenPrice('');
                      }
                    }}
                    className="rounded"
                  />
                  <label className="text-sm text-gray-700">価格設定ルールを使用</label>
                </div>
              </div>

              {/* 価格計算結果 */}
              {calculatedPrice && usePricingRules && (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <DollarSign size={18} className="text-green-600" />
                    <span className="font-medium text-sm text-green-800">価格設定に基づく自動計算結果</span>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <div className="text-xs text-gray-500">Amazon仕入価格</div>
                      <div className="text-lg font-bold">¥{calculatedPrice.amazonPrice.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">楽天販売価格</div>
                      <div className="text-lg font-bold text-blue-600">¥{calculatedPrice.rakutenPrice.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">利益額</div>
                      <div className="text-lg font-bold text-green-600">¥{calculatedPrice.profit.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">利益率</div>
                      <div className="text-lg font-bold text-purple-600">{calculatedPrice.profitPercent}%</div>
                    </div>
                  </div>
                  {calculatedPrice.appliedRule && (
                    <div className="mt-2 text-xs text-gray-600">
                      適用ルール: {calculatedPrice.appliedRule.minPrice.toLocaleString()}円以上 → {calculatedPrice.appliedRule.markupPercent}% + {calculatedPrice.appliedRule.markupFixed.toLocaleString()}円
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">楽天商品コード *</label>
                  <input
                    type="text"
                    value={rakutenItemCode}
                    onChange={(e) => setRakutenItemCode(e.target.value)}
                    placeholder="例: amazon-B0795D2LXS"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {usePricingRules ? '価格調整（任意）' : '価格上乗せ率'}
                  </label>
                  {usePricingRules ? (
                    <input
                      type="number"
                      value={rakutenPrice}
                      onChange={(e) => setRakutenPrice(e.target.value)}
                      placeholder={calculatedPrice ? `自動: ${calculatedPrice.rakutenPrice}` : ''}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  ) : (
                    <>
                      <input
                        type="number"
                        value={priceMarkup}
                        onChange={(e) => setPriceMarkup(e.target.value)}
                        step="0.1"
                        min="1.0"
                        className="w-full border rounded-lg px-3 py-2"
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        計算価格: ¥{Math.floor(selectedProduct.price * parseFloat(priceMarkup)).toLocaleString()}
                      </div>
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">楽天在庫数（任意）</label>
                  <input
                    type="number"
                    value={rakutenStock}
                    onChange={(e) => setRakutenStock(e.target.value)}
                    placeholder={`Amazon在庫: ${selectedProduct.stock}`}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ブランド</label>
                  <input
                    type="text"
                    value={selectedProduct.brand}
                    readOnly
                    className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                  />
                </div>
              </div>

              {/* 同意確認文 */}
              {applicableConsents.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="font-medium text-sm text-yellow-800 mb-2">
                    適用される同意確認文（{applicableConsents.length}件）
                  </div>
                  <div className="space-y-2">
                    {applicableConsents.map((consent, i) => (
                      <div key={consent.id || i} className="text-sm text-yellow-700 bg-white p-2 rounded border border-yellow-100">
                        {consent.text}
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-yellow-600 mt-2">
                    ※商品説明に自動追加されます
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded p-4">
                <h4 className="font-medium text-sm mb-2">出品内容プレビュー</h4>
                <div className="text-sm space-y-1">
                  <div><strong>商品名:</strong> {selectedProduct.title}</div>
                  <div><strong>ASIN:</strong> {selectedProduct.asin}</div>
                  <div><strong>ブランド:</strong> {selectedProduct.brand || '未設定'}</div>
                  <div>
                    <strong>販売価格:</strong> 
                    <span className="text-blue-600 font-bold ml-1">
                      ¥{(rakutenPrice || calculatedPrice?.rakutenPrice || Math.floor(selectedProduct.price * parseFloat(priceMarkup))).toLocaleString()}
                    </span>
                    {calculatedPrice && (
                      <span className="text-green-600 ml-2">
                        (利益: ¥{(parseInt(rakutenPrice || calculatedPrice.rakutenPrice) - selectedProduct.price).toLocaleString()})
                      </span>
                    )}
                  </div>
                  <div><strong>在庫数:</strong> {rakutenStock || selectedProduct.stock}</div>
                </div>
              </div>

              <button
                onClick={listToRakuten}
                disabled={loading || !rakutenItemCode}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                <Upload size={20} />
                楽天に出品する
              </button>
            </div>
          )}
        </div>
      )}

      {/* 出品一覧タブ */}
      {activeTab === 'listings' && (
        <div className="space-y-4">
          {/* 統計 */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">出品中</div>
              <div className="text-2xl font-bold text-green-600">{stats.active || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">準備中</div>
              <div className="text-2xl font-bold text-yellow-600">{stats.pending || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">停止</div>
              <div className="text-2xl font-bold text-gray-600">{stats.delisted || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">エラー</div>
              <div className="text-2xl font-bold text-red-600">{stats.error || 0}</div>
            </div>
          </div>

          {/* フィルター・検索 */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="ASIN、商品管理番号で検索..."
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <select className="border rounded-lg px-3 py-2">
                <option value="">ステータス: すべて</option>
                <option value="active">出品中</option>
                <option value="pending">準備中</option>
                <option value="delisted">停止</option>
                <option value="error">エラー</option>
              </select>
              <button
                onClick={fetchListings}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <RefreshCw size={16} />
                更新
              </button>
            </div>
          </div>

          {/* 一覧 */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h3 className="font-medium">出品商品一覧</h3>
                <span className="text-sm text-gray-500">
                  管理商品数: {listings.length}件
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1 text-sm border rounded hover:bg-gray-50">
                  すべて選択
                </button>
                <button className="px-3 py-1 text-sm border rounded hover:bg-gray-50">
                  一括同期
                </button>
                <button className="px-3 py-1 text-sm border border-red-200 text-red-600 rounded hover:bg-red-50">
                  一括停止
                </button>
              </div>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-10">
                    <input type="checkbox" className="rounded" />
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">商品名</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">ブランド</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Link</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">状態</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">価格・在庫</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">登録日</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center">
                      <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                      読み込み中...
                    </td>
                  </tr>
                ) : listings.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      出品商品がありません
                    </td>
                  </tr>
                ) : (
                  listings.map(listing => (
                    <tr key={listing.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3">
                        <input type="checkbox" className="rounded" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                            <Package size={20} className="text-gray-400" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-blue-600 hover:underline cursor-pointer truncate max-w-xs">
                              {listing.rakutenItemName}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">{listing.rakutenItemCode}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600">-</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-1">
                          <a
                            href={`https://www.amazon.co.jp/dp/${listing.asin}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          >
                            Amazon <ExternalLink size={10} />
                          </a>
                          <a
                            href="#"
                            className="text-xs text-red-600 hover:underline flex items-center gap-1"
                          >
                            楽天 <ExternalLink size={10} />
                          </a>
                        </div>
                      </td>
                      <td className="px-3 py-3">{renderStatusBadge(listing.status)}</td>
                      <td className="px-3 py-3">
                        <div className="text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">AMZ:</span>
                            <span className="font-medium">¥{listing.amazonPrice ? Number(listing.amazonPrice).toLocaleString() : '-'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">楽天:</span>
                            <span className="font-medium text-red-600">¥{Number(listing.rakutenPrice).toLocaleString()}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                            <span>在庫: {listing.rakutenStock}</span>
                            {listing.amazonStock !== null && listing.amazonStock !== listing.rakutenStock && (
                              <span className="text-orange-500">(差分あり)</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-500">
                        {new Date(listing.createdAt).toLocaleString('ja-JP', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1">
                          {listing.status === 'active' && (
                            <>
                              <button
                                onClick={() => syncListing(listing.id)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                title="在庫・価格同期"
                              >
                                <RefreshCw size={14} />
                              </button>
                              <button
                                onClick={() => delistItem(listing.id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                title="出品停止"
                              >
                                <XCircle size={14} />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => {
                              setSelectedListing(listing);
                              fetchLogs(listing.id);
                            }}
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                            title="詳細・ログ"
                          >
                            <Eye size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 一括出品タブ */}
      {activeTab === 'bulk' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h3 className="font-medium text-lg">一括出品設定</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amazonショップ</label>
                <select
                  value={selectedAmazonShop}
                  onChange={(e) => setSelectedAmazonShop(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {amazonShops.map(shop => (
                    <option key={shop.id} value={shop.id}>{shop.shopName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">楽天ショップ</label>
                <select
                  value={selectedRakutenShop}
                  onChange={(e) => setSelectedRakutenShop(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {rakutenShops.map(shop => (
                    <option key={shop.id} value={shop.id}>{shop.shopName}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">価格上乗せ率（一括）</label>
              <input
                type="number"
                value={bulkPriceMarkup}
                onChange={(e) => setBulkPriceMarkup(e.target.value)}
                step="0.1"
                min="1.0"
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ASIN + 楽天商品コード（タブまたはカンマ区切り、1行1商品）
              </label>
              <textarea
                value={bulkAsins}
                onChange={(e) => setBulkAsins(e.target.value)}
                placeholder="B0795D2LXS,rakuten-001&#10;B0795D2KZR,rakuten-002&#10;B0795D299L,rakuten-003"
                className="w-full border rounded-lg px-3 py-2 h-64 font-mono text-sm"
              />
              <div className="text-xs text-gray-500 mt-1">
                {bulkAsins.split('\n').filter(l => l.trim()).length}件の商品
              </div>
            </div>

            <button
              onClick={bulkList}
              disabled={loading || !bulkAsins}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              <Upload size={20} />
              一括出品を実行
            </button>
          </div>
        </div>
      )}

      {/* 詳細モーダル */}
      {selectedListing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">出品詳細</h3>
              <button onClick={() => setSelectedListing(null)} className="text-gray-400 hover:text-gray-600">
                <XCircle size={24} />
              </button>
            </div>

            <div className="space-y-6">
              {/* 基本情報 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500">ASIN</div>
                  <div className="font-medium">{selectedListing.asin}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">ステータス</div>
                  {renderStatusBadge(selectedListing.status)}
                </div>
                <div>
                  <div className="text-sm text-gray-500">楽天商品コード</div>
                  <div className="font-medium">{selectedListing.rakutenItemCode}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">最終同期</div>
                  <div className="font-medium">
                    {selectedListing.lastSyncAt
                      ? new Date(selectedListing.lastSyncAt).toLocaleString('ja-JP')
                      : '-'}
                  </div>
                </div>
              </div>

              {/* 価格・在庫 */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <h4 className="font-medium text-sm mb-3">価格・在庫情報</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Amazon価格</div>
                    <div className="text-lg font-bold">
                      {selectedListing.amazonPrice ? `¥${Number(selectedListing.amazonPrice).toLocaleString()}` : '-'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">楽天価格</div>
                    <div className="text-lg font-bold text-red-600">
                      ¥{Number(selectedListing.rakutenPrice).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Amazon在庫</div>
                    <div className="font-medium">{selectedListing.amazonStock || '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">楽天在庫</div>
                    <div className="font-medium">{selectedListing.rakutenStock}</div>
                  </div>
                </div>
              </div>

              {/* ログ */}
              <div>
                <h4 className="font-medium text-sm mb-2">操作ログ</h4>
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {listingLogs.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">ログがありません</div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <tbody className="bg-white divide-y divide-gray-200">
                        {listingLogs.map(log => (
                          <tr key={log.id} className="text-sm">
                            <td className="px-3 py-2">
                              {new Date(log.createdAt).toLocaleString('ja-JP')}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-1 rounded text-xs ${
                                log.action === 'list' ? 'bg-blue-100 text-blue-700' :
                                log.action === 'sync' ? 'bg-green-100 text-green-700' :
                                log.action === 'delist' ? 'bg-gray-100 text-gray-700' :
                                'bg-purple-100 text-purple-700'
                              }`}>
                                {log.action}
                              </span>
                            </td>
                            <td className="px-3 py-2">{log.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setSelectedListing(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

